import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Crown, RefreshCw, XCircle } from 'lucide-react'
import {
  cancelSubscription, renewSubscription, setAutoRenew, formatCredits,
  type CatalogPlan, type SubscriptionStatus,
} from '@/api/billing'
import { useRazorpayCheckout } from '@/hooks/useRazorpayCheckout'

/**
 * The current plan, its included credits, and the two controls a subscriber
 * actually needs: auto-renewal and cancellation.
 */
export default function PlanCard({
  plan, subscription, onChanged,
}: {
  plan: CatalogPlan | null
  subscription: SubscriptionStatus | null
  onChanged: () => void
}) {
  const navigate = useNavigate()
  const { checkout, busy: paying } = useRazorpayCheckout()
  const [busy, setBusy] = useState(false)
  const [confirmCancel, setConfirmCancel] = useState(false)

  const isPaid = !!subscription?.status && (plan?.code ?? 'F') !== 'F'
  // Expired / expiring states where "Renew now" is the action that matters —
  // including an auto-renewing subscription whose renewal never arrived.
  const needsRenewal = isPaid && !!subscription &&
    ['past_due', 'in_grace', 'lapsed'].includes(subscription.status || '')

  const renewNow = async () => {
    const result = await checkout({
      purpose: 'plan_renewal',
      description: `Renew ${plan?.display_name || 'subscription'}`,
      precreate: () => renewSubscription(),
    })
    if (result.cancelled) return
    if (!result.ok) {
      toast.error(result.error || 'The renewal payment did not complete')
      return
    }
    toast.success('Subscription renewed')
    onChanged()
  }

  const toggleAutoRenew = async () => {
    if (!subscription) return
    setBusy(true)
    try {
      const next = await setAutoRenew(!subscription.auto_renew)
      if (next.requires_checkout) {
        // The flag is on, but nothing can actually charge a renewal — say so
        // and offer the checkout that arms it, rather than pretending.
        toast(next.detail ||
          'Auto-renewal is on, but a renewal checkout is needed to set up automatic charging.',
          { icon: '⚠️', duration: 8000 })
      } else {
        toast.success(subscription.auto_renew ? 'Auto-renewal turned off' : 'Auto-renewal turned on')
      }
      onChanged()
    } catch {
      toast.error('Could not update auto-renewal')
    } finally {
      setBusy(false)
    }
  }

  const doCancel = async () => {
    setBusy(true)
    try {
      await cancelSubscription()
      toast.success('Subscription cancelled — you keep access until the period ends')
      setConfirmCancel(false)
      onChanged()
    } catch {
      toast.error('Could not cancel the subscription')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card" style={{ padding: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <Crown size={16} style={{ color: 'var(--color-accent)' }} />
        <h3 style={{ margin: 0, fontSize: '1.0625rem' }}>Your plan</h3>
      </div>

      <div style={{ fontSize: '1.3125rem', fontWeight: 700 }}>{plan?.display_name || '—'}</div>
      {plan && plan.included_monthly_credits > 0 && (
        <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '0.125rem' }}>
          Includes {formatCredits(plan.included_monthly_credits)} credits each month.
        </div>
      )}
      {plan?.description && (
        <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}>
          {plan.description}
        </p>
      )}

      {isPaid && subscription && (
        <label style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          fontSize: '0.875rem', marginTop: '0.75rem',
        }}>
          <input
            type="checkbox"
            checked={!!subscription.auto_renew}
            onChange={toggleAutoRenew}
            disabled={busy}
          />
          Renew automatically
        </label>
      )}

      {needsRenewal && (
        <div style={{
          marginTop: '0.75rem', padding: '0.625rem 0.75rem', borderRadius: 8,
          background: 'color-mix(in srgb, var(--color-warning, #f0a020) 12%, transparent)',
          border: '1px solid var(--color-warning, #f0a020)', fontSize: '0.875rem',
        }}>
          {subscription?.status === 'lapsed'
            ? 'This subscription has lapsed.'
            : `This subscription expired${subscription?.current_period_end
                ? ` on ${new Date(subscription.current_period_end).toLocaleDateString()}`
                : ''}${subscription?.auto_renew
                ? ' and the automatic renewal has not gone through' : ''}.`}
          {' '}Renew now to keep full access.
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
        {needsRenewal && (
          <button className="btn btn-primary" onClick={renewNow} disabled={paying}>
            <RefreshCw size={14} /> Renew now
          </button>
        )}
        <button className={needsRenewal ? 'btn btn-secondary' : 'btn btn-primary'}
                onClick={() => navigate('/payments/plans')}>
          {isPaid ? 'Change plan' : 'See plans'}
        </button>
        {isPaid && !subscription?.cancel_at_period_end && (
          <button className="btn btn-secondary" onClick={() => setConfirmCancel(true)} disabled={busy}>
            <XCircle size={14} /> Cancel
          </button>
        )}
      </div>

      {subscription?.cancel_at_period_end && (
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-warning, #f0a020)', marginTop: '0.625rem' }}>
          Cancelled — access continues until the end of the current period.
        </div>
      )}

      {confirmCancel && (
        <div className="overlay" onClick={(e) => { if (e.target === e.currentTarget) setConfirmCancel(false) }}>
          <div className="modal animate-slide-up" style={{ maxWidth: 400, width: '95%' }}>
            <div className="modal-header"><h3 className="modal-title">Cancel subscription?</h3></div>
            <p style={{ fontSize: '0.9375rem', color: 'var(--color-text-secondary)' }}>
              You keep full access until{' '}
              {subscription?.current_period_end
                ? new Date(subscription.current_period_end).toLocaleDateString()
                : 'the end of the current period'}
              . Credits you have already bought are not affected.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setConfirmCancel(false)}>Keep it</button>
              <button className="btn btn-danger" onClick={doCancel} disabled={busy}>
                Cancel subscription
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
